import { createSelector, createStructuredSelector } from 'reselect';
import { t } from '@lingui/macro';

import {
  CITIZENSHIP,
  DOCUMENT_TYPE,
  DocumentFields,
  DocumentTypeValue,
  PASSPORT_STATUS,
} from '../constants/document';
import {
  BORN_IN,
  BORN_IN_MARRIAGE,
  CHILD_ADDRESS,
  CONFIRMING_DOCUMENTS,
} from '../constants/child';
import { getValue } from '../constants/getFieldValues';
import { Elk, ELKChild, Relative } from '../models/elk';
import {
  DEAD_PARENT,
  SECOND_PARENT_STATUS,
} from '../constants/secondParentStatus';
import {
  isAdult,
  isUnder23YearsOld,
  shouldBeGettingEducation,
  shouldHaveIdentification,
} from '../helpers/dateHelpers';
import {
  getPassportDueDate,
  isCitizen,
  getPassportStatus,
  isResident,
  isRussianPassport,
} from '../validators/common';
import { ChildFields, DeadSecondParent, SecondParent } from '../models/child';
import { AddressFields } from '../../types/address';
import { Field } from '../declarations/widget-ums';
import { EMPTY_FIELD } from '../constants/emptyField';
import { fullAddressWithFlat, getManualAddress } from './address';
import {
  getSpouseAddress,
  isSpouseAddressMatchWithApplicant,
  isSpouseRegisteredInMoscow,
} from './spouse';
import { i18n } from '../helpers/i18n';
import { PersonFields } from '../models/person';
import {
  getApplicantAddress,
  getApplicantMarriedOrLivingTogether,
} from './applicant';
import { ApplicantFields } from '../models/applicant';

export const child: ({ child }: { child: ChildFields }) => ChildFields = (
  state
) => state.child;

const DATE_LENGTH = 8;

export const childHasBirthdate: (state: {
  child: ChildFields;
}) => false | boolean = createSelector(child, ({ birthdate: { value } }) => {
  return Boolean(value) && value.replace(/\D/g, '').length === DATE_LENGTH;
});

export const isChildBornInCurrentMarriage = ({
  bornInCurrentMarriage: { value },
}): boolean => value === BORN_IN.currentMarriage;

export const childAddressMatchesSpouse = ({
  addressMatchWithApplicant: { value },
}): boolean => value === CHILD_ADDRESS.matchWithSpouse;

export const getChildAddress: (state: {
  child: ChildFields;
}) => AddressFields = createSelector(child, ({ address }) => address);

export const getChildCitizenship: (state: {
  child: ChildFields;
}) => CITIZENSHIP = createSelector(
  child,
  ({ citizenship: { value } }) => value
);

export const getChildBirthdate: (state: {
  child: ChildFields;
}) => string = createSelector(child, ({ birthdate }) => getValue(birthdate));

export const isChild18YearsOld: (state: {
  child: ChildFields;
}) => boolean = createSelector(child, ({ birthdate: { value } }) => {
  return isAdult(value);
});

export const isChildRegisteredInMoscow = createSelector(
  child,
  ({ isRegisteredInMoscow }) => isRegisteredInMoscow
);

function getChildFromList(
  child,
  list: (ELKChild | Relative)[]
): ELKChild | Relative | undefined {
  return list.find(({ item_id: itemId }) => itemId === child.id);
}

export const getActiveChildFromElk: (
  state: { child: ChildFields } & { elk?: Elk }
) => ELKChild | Relative | undefined = createSelector(
  child,
  ({ elk }: { elk: Elk }) => {
    return elk.children;
  },
  getChildFromList
);

export const getActiveChildFromRelatives = createSelector(
  child,
  ({ elk }: { elk: Elk }) => {
    return elk.relatives;
  },
  getChildFromList
);

export const getActiveChildBirthCertificate: (state: {
  child: ChildFields;
  elk: Elk;
}) => DocumentFields = createSelector(
  child,
  getActiveChildFromElk,
  ({ birthCertificate, id }, childFromElk: ELKChild) => {
    if (!id || !childFromElk) return birthCertificate;
    const type = getValue<DocumentTypeValue>(birthCertificate.type);
    if (!type) return birthCertificate;
    if (type === DOCUMENT_TYPE.BIRTH_CERTIFICATE_INTERNATIONAL) {
      return birthCertificate; // TODO - после починки получения данных иностранного СОР из ЕЛК удалить
      const {
        foreign_birth_certificate_number: number,
        birth_certificate_number: rfNumber,
        birth_certificate_issued_at: issueDate,
        birth_certificate_issued_by: issuer,
      } = childFromElk;
      if (!number) return birthCertificate;

      return Object.assign(
        {},
        birthCertificate,
        {
          type: birthCertificate.type,
          number: { value: number, disabled: !!number },
          birthCertificate: true,
        },
        {
          issuer: issuer
            ? { value: issuer, disabled: !!issuer }
            : birthCertificate.issuer,
        },
        {
          issueDate: issueDate
            ? { value: issueDate, disabled: !!issueDate }
            : birthCertificate.issueDate,
        }
      );
    }
    if (type === DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF) {
      const {
        birth_certificate_number: number,
        birth_certificate_issued_at: issueDate,
        birth_certificate_issued_by: issuer,
      } = childFromElk;
      if (!number) {
        return birthCertificate;
      }

      return {
        type: birthCertificate.type,
        number: { value: number.toUpperCase(), disabled: true },
        issuer: issuer
          ? { value: issuer, disabled: !!issuer }
          : birthCertificate.issuer,
        issueDate: issueDate
          ? { value: issueDate, disabled: !!issueDate }
          : birthCertificate.issueDate,
        birthCertificate: true,
        divisionCode: EMPTY_FIELD,
      };
    }
  }
);

export const childShouldHaveBirthCertificate: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildBirthCertificate,
  ({ type }) => !!type.value
);

export const childShouldHaveRussianBirthCertificate: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildBirthCertificate,
  ({ type: { value } }) => value === DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF
);

export const getActiveChildPassport: (state: {
  child: ChildFields;
  elk: Elk;
}) => DocumentFields = createSelector(
  child,
  getActiveChildFromRelatives,
  (child, childFromRelatives) => {
    if (isResident(getValue(child.citizenship)) || !childFromRelatives)
      return child.dul;
    const {
      passport_number: number,
      passport_issued_at: issueDate,
      passport_issued_by: issuer,
      passport_division_code: divisionCode,
    } = childFromRelatives as Relative;
    return Object.assign(
      {},
      child.dul,
      { type: { value: DOCUMENT_TYPE.PASSPORT_RF, disabled: true } },
      number ? { number: { value: number, disabled: true } } : {},
      issueDate ? { issueDate: { value: issueDate, disabled: true } } : {},
      issuer ? { issuer: { value: issuer, disabled: true } } : {},
      divisionCode
        ? { divisionCode: { value: divisionCode, disabled: true } }
        : {}
    );
  }
);

const getActiveChildPassportIssueDate: (state: {
  child: ChildFields;
  elk: Elk;
}) => string = createSelector(getActiveChildPassport, ({ issueDate }) =>
  getValue(issueDate)
);

export const childHasRussianPassport = (childPassport): boolean => {
  return ['number', 'issueDate', 'issuer', 'divisionCode'].every((key) => {
    return !!getValue(childPassport[key]);
  });
};

export const childHasActivePassport: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getChildBirthdate,
  getActiveChildPassport,
  (birthdate, childPassport) => {
    return (
      shouldHaveIdentification(birthdate) ||
      childHasRussianPassport(childPassport)
    );
  }
);

export const childShouldHaveRussianPassport: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  getActiveChildPassport,
  ({ citizenship }, { type }) =>
    isCitizen(getValue(citizenship)) && isRussianPassport(type.value)
);

export const childShouldHavePassport: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  childHasActivePassport,
  ({ citizenship, birthdate }, hasActivePassport) => {
    if (isCitizen(getValue(citizenship))) {
      return hasActivePassport;
    }

    return isResident(getValue(citizenship));
  }
);

export const getActiveChildSecondParent: (state: {
  child: ChildFields;
}) => SecondParent = createSelector(child, ({ secondParent }) => secondParent);

export const getActiveChildSecondParentStatusValue: (state: {
  child: ChildFields;
}) => string = createSelector(getActiveChildSecondParent, ({ status }) =>
  getValue(status)
);

export const isSecondParentDivorced: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildSecondParentStatusValue,
  (value) => value === SECOND_PARENT_STATUS.divorced
);

export const isPaternity: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildSecondParentStatusValue,
  (value) => value === SECOND_PARENT_STATUS.paternity
);

export const isWidowed: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildSecondParentStatusValue,
  (value) => value === SECOND_PARENT_STATUS.widowed
);

export const hasDeathCertificate: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  getActiveChildSecondParent,
  ({ proofOfDeath }: DeadSecondParent) =>
    proofOfDeath.value === DEAD_PARENT.hasDeathCert
);

export const childAddressMatchesNoOne: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  ({ addressMatchWithApplicant: { value } }) =>
    value === CHILD_ADDRESS.notMatchWithSpouse
);

export const shouldValidateChildAddress: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  isChildRegisteredInMoscow,
  ({ addressMatchWithApplicant: { value } }, isRegisteredInMoscow) =>
    value === CHILD_ADDRESS.notMatchWithSpouse && isRegisteredInMoscow
);

export const secondParentStatus: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  isSecondParentDivorced,
  isWidowed,
  (isDivorced, isWidowed) => {
    return isDivorced || isWidowed;
  }
);

export const isPaternityOrDivorced: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  isSecondParentDivorced,
  isPaternity,
  (isDivorced, isPaternity) => isDivorced || isPaternity
);

export const childHasConfirmingDocs: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  ({ confirmingDocs: { value } }) => value === CONFIRMING_DOCUMENTS.indirectly
);
export const getSelectedParent: (state: {
  child: ChildFields;
}) => Field<string> = createSelector(
  child,
  ({ selectedParent }) => selectedParent
);

export const getShowSecondParentContent: (state: {
  child: ChildFields;
}) => boolean = createSelector(
  child,
  ({ showSecondParentContent }) => showSecondParentContent
);

export const getPolyclinicIsFoundInCatalog = createSelector(
  child,
  ({ polyclinic: { isFoundInCatalog } }) => isFoundInCatalog
);

export const getEducationIsFoundInCatalog = createSelector(
  child,
  ({ education: { isFoundInCatalog } }) => isFoundInCatalog
);

export const isAdultSelector = createSelector(child, ({ birthdate }) =>
  isAdult(getValue(birthdate))
);

export const isUnder23YearsOldSelector = createSelector(
  child,
  ({ birthdate }) => isUnder23YearsOld(getValue(birthdate))
);

export const shouldBeGettingEducationSelector = createSelector(
  child,
  ({ birthdate }) => shouldBeGettingEducation(getValue(birthdate))
);

export const showHomestayDocumentsSelector = createSelector(
  childHasConfirmingDocs,
  childAddressMatchesNoOne,
  isPaternityOrDivorced,
  isAdultSelector,
  (indirectConfirmingDocs, addressNotMatch, parentStatus, isAdult) => {
    return (
      addressNotMatch && parentStatus && indirectConfirmingDocs && !isAdult
    );
  }
);

export const showEducationSelector = createSelector(
  isUnder23YearsOldSelector,
  shouldBeGettingEducationSelector,
  showHomestayDocumentsSelector,
  (isUnder23YearsOld, shouldBeGettingEducation, showHomestayDocuments) =>
    showHomestayDocuments || (shouldBeGettingEducation && isUnder23YearsOld)
);

export const showAddressHints = createStructuredSelector<any, any>({
  applicantAddress: (state) => {
    return getManualAddress(state.applicant.address);
  },
  spouseAddress: (state) => {
    const isSpouseInMoscow = isSpouseRegisteredInMoscow(state);
    const spouseAddressMatchesApplicant = isSpouseAddressMatchWithApplicant(
      state
    );
    const isAddressCompleted = state.spouse.address.isAddressCompleted;

    if (spouseAddressMatchesApplicant)
      return fullAddressWithFlat(state.applicant);
    if (!isSpouseInMoscow) return i18n._(t`Адрес не в Москве`);
    if (!isAddressCompleted) return i18n._(t`Адрес не указан`);
    return getManualAddress(state.spouse.address);
  },
});

export const showSpouseAddressOption: (state: {
  applicant: ApplicantFields;
  spouse: PersonFields;
}) => boolean = createSelector(
  getApplicantAddress,
  getSpouseAddress,
  isSpouseAddressMatchWithApplicant,
  getApplicantMarriedOrLivingTogether,
  (
    applicantAddress,
    spouseAddress,
    doesAddressesMatch,
    applicantMarriedOrLivivingWith
  ) => {
    if (!applicantMarriedOrLivivingWith) return false;
    const spouseFilledSameAddress =
      getManualAddress(applicantAddress) === getManualAddress(spouseAddress);
    if (doesAddressesMatch) return !doesAddressesMatch;
    return !spouseFilledSameAddress;
  }
);

export const isApplicantChild = createSelector(
  child,
  ({ bornInNotCurrentMarriage: { value } }) =>
    value === BORN_IN_MARRIAGE.ofApplicant
);

export const childConfirmingDocsSelector = createSelector(
  child,
  isAdultSelector,
  (
    {
      confirmingDocs,
      addressMatchWithApplicant: { value: addressMatchWithApplicantValue },
      secondParent: {
        status: { value: statusValue },
      },
    },
    isAdult
  ) => {
    return {
      isVisible:
        !isAdult &&
        addressMatchWithApplicantValue === CHILD_ADDRESS.notMatchWithSpouse &&
        (statusValue === SECOND_PARENT_STATUS.paternity ||
          statusValue === SECOND_PARENT_STATUS.divorced),
      confirmingDocs,
    };
  }
);

export const childAddressFulfilled = createSelector(
  child,
  isAdultSelector,
  ({ addressMatchWithApplicant, address, isRegisteredInMoscow }, isAdult) =>
    isAdult ||
    [CHILD_ADDRESS.matchWithSpouse, CHILD_ADDRESS.matchWithApplicant].includes(
      addressMatchWithApplicant.value!
    ) ||
    address.isAddressCompleted ||
    !isRegisteredInMoscow
);

export const validateActiveChildFromRelativesPassportStatus: (state: {
  child: ChildFields;
  elk: Elk;
}) => string = createSelector(
  getActiveChildFromRelatives,
  childShouldHaveRussianPassport,
  (child: Relative, isPassportRf) => {
    if (child && child.birthdate && child.passport_issued_at) {
      const { birthdate, passport_issued_at } = child;
      return getPassportStatus(birthdate, passport_issued_at, isPassportRf);
    }
    return PASSPORT_STATUS.VALID;
  }
);

export const validateChildPassportStatus: (state: {
  child: ChildFields;
}) => string = createSelector(
  getChildBirthdate,
  getActiveChildPassportIssueDate,
  childShouldHaveRussianPassport,
  (birthDate, issueDate, isPassportRf) =>
    getPassportStatus(birthDate, issueDate, isPassportRf)
);

export const getChildPassportDueDate = createSelector(
  getChildBirthdate,
  (birthdate) => getPassportDueDate(birthdate)
);

export const getChildPassportStatus = createSelector(
  getActiveChildPassport,
  ({ status }) => status
);
